import { Controller } from "@hotwired/stimulus"

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Connects to data-controller="ui--modal"
// Uses native HTML <dialog> element with Stimulus enhancement
export default class extends Controller {
  static targets = ["panel"]
  static values = {
    open: Boolean,
    dismissible: { type: Boolean, default: true },
  }

  connect() {
    this._previouslyFocused = null
    // Guards re-entrancy: a single dismiss can invoke close() more than once
    // (e.g. a rapid double-click on the backdrop, or overlapping wired actions)
    // — the flag keeps the animate-out + before-close veto from running twice.
    // NB: the booking modals open NON-modal (the `open` attribute, not
    // showModal), so Escape has no native cancel→close path to race with; it
    // routes solely through handleEscape → close(), which the veto can stop.
    this._closing = false

    // Auto-open if open value is true
    if (this.openValue && !this.element.open) {
      this.open()
    }

    // Listen for escape key (backup, dialog handles this natively)
    this.escapeHandler = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.escapeHandler)

    // Focus trap handler
    this._trapFocusHandler = this._trapFocus.bind(this)
    this.element.addEventListener("keydown", this._trapFocusHandler)

    // Listen for native dialog close event
    this.element.addEventListener("close", this.handleNativeClose.bind(this))
  }

  disconnect() {
    document.removeEventListener("keydown", this.escapeHandler)
    this.element.removeEventListener("keydown", this._trapFocusHandler)
    // Reset any leftover body scroll-lock. If the modal is unmounted while
    // still open (Turbo navigation, manual DOM removal), the close handler
    // never fires and body stays overflow:hidden — making the next page
    // unscrollable.
    if (this.element.open) {
      document.body.style.overflow = ""
    }
  }

  open() {
    if (!this.element.open) {
      // Store the element that had focus before opening
      this._previouslyFocused = document.activeElement

      this.element.showModal()
      document.body.style.overflow = "hidden"

      // Animate in
      this.animateIn()

      // Set initial focus: [autofocus] element, or first focusable, or the panel itself
      requestAnimationFrame(() => {
        const autofocusEl = this.element.querySelector("[autofocus]")
        if (autofocusEl) {
          autofocusEl.focus()
        } else {
          const firstFocusable = this.element.querySelector(FOCUSABLE_SELECTOR)
          if (firstFocusable) {
            firstFocusable.focus()
          }
        }
      })

      // Dispatch custom open event
      this.element.dispatchEvent(
        new CustomEvent("modal:open", { bubbles: true }),
      )
    }
  }

  close(event) {
    // Prevent closing if not dismissible.
    //
    // `dismissible` governs how the USER may dismiss the modal, so the veto
    // applies only to the paths a user drives — the X, Escape and a backdrop
    // click all arrive here carrying their event. A host that wires its own
    // buttons calls this with NO event (`controller.close()`), and that close
    // is the host's decision, not a dismissal: vetoing it leaves a
    // non-dismissible modal with no way out at all — its own Cancel included.
    if (!this.dismissibleValue && event) {
      event.preventDefault()
      return
    }

    if (this._closing) return

    // Give a nested form (e.g. pipeline--unsaved-changes on the booking form) a
    // chance to veto the close so the user can confirm before discarding edits.
    // Covers every dismiss path — X, Esc and backdrop click all route here.
    // Bubbles to document so a descendant controller can listen via @document.
    const proceed = this.element.dispatchEvent(
      new CustomEvent("modal:before-close", {
        bubbles: true,
        cancelable: true,
      }),
    )
    if (!proceed) return

    this._closing = true

    // Animate out then close
    this.animateOut(() => {
      if (this.element.open) {
        this.element.close()
        document.body.style.overflow = ""

        // Return focus to the element that triggered the modal
        if (
          this._previouslyFocused &&
          typeof this._previouslyFocused.focus === "function"
        ) {
          this._previouslyFocused.focus()
          this._previouslyFocused = null
        }

        // Dispatch custom close event
        this.element.dispatchEvent(
          new CustomEvent("modal:close", { bubbles: true }),
        )
      }
      this._closing = false
    })
  }

  // Close when the user clicks the overlay/backdrop (outside the panel).
  // Matches clicking the X or Cancel. Delegates to close() so the
  // dismissible guard and animation are respected.
  backdropClose(event) {
    if (this.hasPanelTarget && !this.panelTarget.contains(event.target)) {
      this.close(event)
    }
  }

  handleEscape(event) {
    if (event.key !== "Escape" || !this.element.open) return

    // Only the TOP of the modal stack answers Escape.
    //
    // Every instance listens on `document`, and a nested modal child is the one
    // thing inertness cannot silence: a `showModal()` child makes the page
    // inert, so the outer dialog's X and backdrop are unreachable, but its
    // keydown listener still fires. Without this check, Escape pressed on a
    // non-dismissible child vetoed its own close and then let the dismissible
    // PARENT under it close — the parent left the top layer, the modal child
    // stayed `open` at 0×0 inside a `display: none` ancestor, and the page
    // stayed inert with nothing left to click: a lockout whose only way out is
    // a reload. A nested dialog follows its parent in document order, so the
    // last `dialog[open]` is the innermost, which is the one the user means.
    const open = document.querySelectorAll("dialog[open]")
    if (open[open.length - 1] !== this.element) return

    if (!this.dismissibleValue) {
      event.preventDefault()
    } else {
      this.close(event)
    }
  }

  handleNativeClose() {
    // Cleanup when dialog is closed by any means
    document.body.style.overflow = ""

    // Return focus on native close
    if (
      this._previouslyFocused &&
      typeof this._previouslyFocused.focus === "function"
    ) {
      this._previouslyFocused.focus()
      this._previouslyFocused = null
    }
  }

  // Trap focus within the modal when Tab is pressed
  _trapFocus(event) {
    if (event.key !== "Tab" || !this.element.open) return

    const focusableElements = Array.from(
      this.element.querySelectorAll(FOCUSABLE_SELECTOR),
    )
    if (focusableElements.length === 0) return

    const firstFocusable = focusableElements[0]
    const lastFocusable = focusableElements[focusableElements.length - 1]

    if (event.shiftKey) {
      // Shift+Tab: if on first element, wrap to last
      if (document.activeElement === firstFocusable) {
        event.preventDefault()
        lastFocusable.focus()
      }
    } else {
      // Tab: if on last element, wrap to first
      if (document.activeElement === lastFocusable) {
        event.preventDefault()
        firstFocusable.focus()
      }
    }
  }

  animateIn() {
    // Fade in and scale animation matching @a-safe-digital
    if (this.hasPanelTarget) {
      this.panelTarget.style.opacity = "0"
      this.panelTarget.style.transform = "translateY(1rem) scale(0.95)"

      requestAnimationFrame(() => {
        this.panelTarget.style.transition = "all 300ms ease-out"
        this.panelTarget.style.opacity = "1"
        this.panelTarget.style.transform = "translateY(0) scale(1)"
      })
    }
  }

  animateOut(callback) {
    // Fade out and scale animation
    if (this.hasPanelTarget) {
      this.panelTarget.style.transition = "all 200ms ease-in"
      this.panelTarget.style.opacity = "0"
      this.panelTarget.style.transform = "translateY(1rem) scale(0.95)"

      setTimeout(() => {
        if (callback) callback()
      }, 200)
    } else {
      if (callback) callback()
    }
  }

  // Prevent clicks inside panel from closing modal
  panelClick(event) {
    event.stopPropagation()
  }
}
